import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus, Briefcase, GraduationCap, Trash, Buildings, CircleNotch, Path, MapPin,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";

function AddJobForm({ onDone }) {
  const [f, setF] = useState({ org_name: "", title: "", location: "", start_year: "", end_year: "", current: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.org_name.trim()) return toast.error("Enter the organization name");
    setBusy(true);
    try {
      await api.post("/me/work-history", f);
      toast.success("Added to your career");
      setF({ org_name: "", title: "", location: "", start_year: "", end_year: "", current: false });
      onDone();
    } catch { toast.error("Couldn't add — try again"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-border/60 bg-white p-5 shadow-sm" data-testid="add-job-form">
      <h3 className="mb-3 flex items-center gap-2 font-heading text-base"><Briefcase size={18} className="text-rose-600" /> Add where you worked</h3>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <Input value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} placeholder="Organization (e.g. Infosys)" data-testid="job-org" className="rounded-xl" />
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Your role / title" data-testid="job-title" className="rounded-xl" />
        <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Location (city)" data-testid="job-location" className="rounded-xl" />
        <div className="flex gap-2">
          <Input value={f.start_year} onChange={(e) => setF({ ...f, start_year: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="From (year)" data-testid="job-start" className="rounded-xl" />
          <Input value={f.end_year} onChange={(e) => setF({ ...f, end_year: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="To (year)" data-testid="job-end" disabled={f.current} className="rounded-xl" />
        </div>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Checkbox checked={f.current} onCheckedChange={(v) => setF({ ...f, current: !!v, end_year: v ? "" : f.end_year })} data-testid="job-current" /> I currently work here
      </label>
      <Button type="submit" disabled={busy} className="mt-4 gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="job-submit">
        {busy ? <CircleNotch size={16} className="animate-spin" /> : <UserPlus size={16} weight="bold" />} Add & find workmates
      </Button>
      <p className="mt-2 text-[11px] text-muted-foreground">We'll create the organization's page automatically and enrich it with an AI draft profile.</p>
    </form>
  );
}

export default function Workmate() {
  const { user } = useAuth();
  const [work, setWork] = useState([]);
  const [journey, setJourney] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo("Workmate Alumni", "Stay connected with the people you worked with. Build your career history, discover former colleagues, and see your Life Journey from school to career.", null, { canonicalPath: "/workmate" });

  const load = () => {
    if (!user) { setLoading(false); return; }
    Promise.all([
      api.get("/me/work-history").then((r) => setWork(r.data.work_history || [])).catch(() => {}),
      api.get(`/profiles/${user.id}/journey`).then((r) => setJourney(r.data.journey || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, [user]);

  const removeJob = async (id) => {
    try { await api.delete(`/me/work-history/${id}`); toast.success("Removed"); load(); } catch { toast.error("Couldn't remove"); }
  };

  return (
    <div className="bg-background" data-testid="workmate-page">
      <div className="border-b border-border/60 bg-gradient-to-br from-rose-50 via-background to-blue-50/50">
        <div className="mx-auto max-w-4xl px-5 py-10">
          <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-rose-600/80"><UserPlus size={16} weight="fill" /> Workmate Alumni</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#1e3a5f] sm:text-5xl">Stay connected with the people you worked with.</h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">Beyond classmates — build your professional history, reconnect with former colleagues, and see your whole journey from school to career in one place.</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-8">
        {!user ? (
          <div className="rounded-2xl border border-dashed border-border/70 bg-white p-8 text-center" data-testid="workmate-signin">
            <Briefcase size={30} weight="duotone" className="mx-auto text-rose-600" />
            <p className="mt-3 text-sm text-muted-foreground">Sign in to build your career history and discover your workmates.</p>
            <Link to="/login?next=%2Fworkmate"><Button className="mt-4 gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="workmate-login-btn"><UserPlus size={16} /> Sign in to start</Button></Link>
          </div>
        ) : loading ? (
          <div className="grid place-items-center py-16"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="space-y-5">
              <AddJobForm onDone={load} />
              <div data-testid="career-list">
                <h2 className="mb-3 font-heading text-lg">Your career</h2>
                {work.length === 0 ? <p className="text-sm text-muted-foreground">No roles yet — add your first above.</p> : (
                  <div className="space-y-2.5">
                    {work.map((w) => (
                      <div key={w.id} className="flex items-center gap-3 rounded-2xl border border-border/60 bg-white p-4 shadow-sm" data-testid={`career-item-${w.id}`}>
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-rose-50 text-rose-600"><Buildings size={18} weight="duotone" /></span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold">{w.title || "Role"}</p>
                          <Link to={`/organizations/${w.org_id}`} className="truncate text-xs text-primary hover:underline">{w.org_name}</Link>
                          <p className="text-[11px] text-muted-foreground">{[w.start_year, w.current ? "Present" : w.end_year].filter(Boolean).join(" – ")}{w.location ? ` · ${w.location}` : ""}</p>
                        </div>
                        <button onClick={() => removeJob(w.id)} className="text-muted-foreground hover:text-red-600" data-testid={`career-del-${w.id}`} aria-label="Remove"><Trash size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div data-testid="life-journey">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg"><Path size={20} weight="duotone" className="text-blue-600" /> Your Life Journey</h2>
              {journey.length === 0 ? <p className="text-sm text-muted-foreground">Add your education and roles to see your journey unfold.</p> : (
                <ol className="relative ml-3 border-l-2 border-border/60">
                  {journey.map((it, i) => (
                    <li key={i} className="mb-6 ml-5" data-testid={`journey-${it.kind}-${i}`}>
                      <span className={`absolute -left-[9px] grid h-4 w-4 place-items-center rounded-full ${it.kind === "education" ? "bg-emerald-500" : "bg-rose-500"}`} />
                      <p className="text-xs font-bold text-muted-foreground">{it.start_year || "—"}{it.end_year ? ` – ${it.end_year}` : it.current ? " – Present" : ""}</p>
                      <p className="mt-0.5 text-sm font-semibold text-[#1e3a5f]">{it.title}</p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">{it.kind === "education" ? <GraduationCap size={13} /> : <Briefcase size={13} />} {it.org_name}{it.subtitle ? ` · ${it.subtitle}` : ""}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
